import React, { useState, useEffect, useMemo } from "react";
import { Live, LiveJump } from "@/common/model/Live";
import { XMarkIcon, ChevronUpIcon, ChevronDownIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { ENDPOINTS } from "@/constants/api";
import { LBSubscriptionService, LBSubscriptionStatus } from "@/services/LBSubscriptionService";
import { LiveService } from "@/services/LiveService";
import { useAuth } from "@/context/AuthContext";
import { DeeplinkButton } from "@/components/ui/DeeplinkButton";

type SortColumn = "nr" | "time" | "height" | "airtime" | "distance" | "speed";
type SortDirection = "asc" | "desc";

const DEFAULT_PROFILE_PIC = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236b7280'%3E%3Ccircle cx='12' cy='12' r='12' fill='%231f2937'/%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%236b7280'/%3E%3C/svg%3E";

// Device icons
const AndroidIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
  </svg>
);

const AppleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const WatchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
  </svg>
);

const PhoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
  </svg>
);

// Get device info for display
const getDeviceInfo = (device: Live["device"]) => {
  if (!device) return null;

  const os = device.os?.toLowerCase() || "";
  const brand = device.brand?.toLowerCase() || "";
  const model = (device.model || "").toLowerCase();

  const isWatch = os.includes("watch") || os.includes("wear") || brand.includes("garmin") || os.includes("garmin");
  const isApple = brand.includes("apple") || os.includes("ios") || os.includes("iphone") || model.includes("iphone") || model.includes("ipad");

  return {
    isWatch,
    isApple,
    isAndroid: !isApple,
    label: device.brand || "Unknown",
    model: device.model,
  };
};

// Get connection status based on last update time
const getConnectionStatus = (lastGpsUpdate: number | string | undefined, lastJumpUpdate: number | string | undefined): {
  status: "online" | "warning" | "offline";
  color: string;
  bgColor: string;
  textColor: string;
  label: string;
} => {
  const gpsTime = lastGpsUpdate ? (typeof lastGpsUpdate === "string" ? new Date(lastGpsUpdate).getTime() : lastGpsUpdate) : 0;
  const jumpTime = lastJumpUpdate ? (typeof lastJumpUpdate === "string" ? new Date(lastJumpUpdate).getTime() : lastJumpUpdate) : 0;
  const lastUpdate = Math.max(gpsTime, jumpTime);

  if (!lastUpdate) {
    return { status: "offline", color: "#ef4444", bgColor: "bg-red-500/20", textColor: "text-red-400", label: "Offline" };
  }

  const diffMins = Math.floor((Date.now() - lastUpdate) / 60000);

  if (diffMins < 1) {
    return { status: "online", color: "#10b981", bgColor: "bg-green-500/20", textColor: "text-green-400", label: "Online" };
  } else if (diffMins <= 3) {
    return { status: "warning", color: "#f59e0b", bgColor: "bg-amber-500/20", textColor: "text-amber-400", label: "Idle" };
  } else {
    return { status: "offline", color: "#ef4444", bgColor: "bg-red-500/20", textColor: "text-red-400", label: "Offline" };
  }
};

// Country code to name mapping
const COUNTRY_NAMES: Record<string, string> = {
  AE: "UAE", AF: "Afghanistan", AL: "Albania", AR: "Argentina", AT: "Austria",
  AU: "Australia", BE: "Belgium", BG: "Bulgaria", BR: "Brazil", CA: "Canada",
  CH: "Switzerland", CL: "Chile", CN: "China", CO: "Colombia", CR: "Costa Rica",
  CU: "Cuba", CV: "Cape Verde", CY: "Cyprus", CZ: "Czech Republic", DE: "Germany",
  DK: "Denmark", DO: "Dominican Republic", DZ: "Algeria", EC: "Ecuador", EE: "Estonia",
  EG: "Egypt", ES: "Spain", FI: "Finland", FJ: "Fiji", FR: "France", GB: "UK",
  GR: "Greece", HK: "Hong Kong", HR: "Croatia", HU: "Hungary", ID: "Indonesia",
  IE: "Ireland", IL: "Israel", IN: "India", IS: "Iceland", IT: "Italy", JP: "Japan",
  KE: "Kenya", KR: "South Korea", KW: "Kuwait", LB: "Lebanon", LT: "Lithuania",
  LU: "Luxembourg", LV: "Latvia", MA: "Morocco", MC: "Monaco", MT: "Malta",
  MU: "Mauritius", MV: "Maldives", MX: "Mexico", MY: "Malaysia", NA: "Namibia",
  NL: "Netherlands", NO: "Norway", NZ: "New Zealand", OM: "Oman", PA: "Panama",
  PE: "Peru", PH: "Philippines", PL: "Poland", PR: "Puerto Rico", PT: "Portugal",
  QA: "Qatar", RO: "Romania", RS: "Serbia", RU: "Russia", SA: "Saudi Arabia",
  SC: "Seychelles", SE: "Sweden", SG: "Singapore", SI: "Slovenia", SK: "Slovakia",
  TH: "Thailand", TN: "Tunisia", TR: "Turkey", TW: "Taiwan", TZ: "Tanzania",
  UA: "Ukraine", US: "USA", UY: "Uruguay", VE: "Venezuela", VN: "Vietnam",
  ZA: "South Africa", ZM: "Zambia", ZW: "Zimbabwe",
};

interface JumpDetailsModalProps {
  session: Live;
  onClose: () => void;
}

type TabId = "jumps" | "user-details" | "privacy";

const JumpDetailsModal: React.FC<JumpDetailsModalProps> = ({ session, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>("jumps");
  const [userDetails, setUserDetails] = useState<LBSubscriptionStatus | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [userError, setUserError] = useState<string | null>(null);
  const [showProfilePic, setShowProfilePic] = useState(false);

  // Full session data with all jumps
  const [fullSession, setFullSession] = useState<Live | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);

  const formatHeight = (height: number) => height?.toFixed(1) || "0.0";
  const formatAirtime = (airtime: number) => airtime?.toFixed(2) || "0.00";
  const formatDistance = (distance: number) => distance?.toFixed(1) || "0.0";
  const formatSpeed = (speed: number) => speed ? (speed * 3.6).toFixed(1) : "0.0";

  const formatTimestamp = (timestamp: number | undefined) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatSessionDuration = (seconds: number | undefined | null) => {
    if (!seconds || seconds <= 0) return "-";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getSessionStartTime = () => {
    if (!session.sessiontime || session.sessiontime <= 0) return "N/A";
    const startTime = new Date(Date.now() - session.sessiontime * 1000);
    return startTime.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const profileSrc = session.user.profilepicid
    ? `${ENDPOINTS.IMAGES.PROFILE}/${session.user.profilepicid}`
    : DEFAULT_PROFILE_PIC;

  // Fetch full session with all jumps when modal opens
  useEffect(() => {
    const fetchFullSession = async () => {
      if (!user?.accesstoken) {
        // No auth - just use data from props
        setIsLoadingSession(false);
        return;
      }

      try {
        const data = await LiveService.getLiveSessionById(session.id, user.accesstoken);
        if (data) {
          setFullSession(data);
        }
        // If no data returned, just use props (no error shown)
      } catch (err: any) {
        // Silently fail and use data from props
        console.warn("Could not fetch full session details, using list data:", err.message);
      } finally {
        setIsLoadingSession(false);
      }
    };

    fetchFullSession();
  }, [session.id, user?.accesstoken]);

  // Sorting state for jumps table
  const [sortColumn, setSortColumn] = useState<SortColumn>("nr");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Use jumps from full session if available, otherwise fall back to session prop
  const sessionJumps = fullSession?.jumps || session.jumps || [];

  // Sort jumps based on current sort settings
  const sortedJumps = useMemo(() => {
    return [...sessionJumps].sort((a, b) => {
      let aVal: number;
      let bVal: number;

      switch (sortColumn) {
        case "nr":
          aVal = a.nr || 0;
          bVal = b.nr || 0;
          break;
        case "time":
          aVal = a.timestampclient || 0;
          bVal = b.timestampclient || 0;
          break;
        case "height":
          aVal = a.height || 0;
          bVal = b.height || 0;
          break;
        case "airtime":
          aVal = a.airtime || 0;
          bVal = b.airtime || 0;
          break;
        case "distance":
          aVal = a.distance || 0;
          bVal = b.distance || 0;
          break;
        case "speed":
          aVal = a.maxspeedreached || 0;
          bVal = b.maxspeedreached || 0;
          break;
        default:
          aVal = a.nr || 0;
          bVal = b.nr || 0;
      }

      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [sessionJumps, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const SortHeader: React.FC<{ column: SortColumn; label: string; align?: "left" | "right" }> = ({ column, label, align = "left" }) => (
    <th
      className={`py-2 px-2 cursor-pointer hover:bg-gray-700 transition-colors select-none ${align === "right" ? "text-right" : "text-left"}`}
      onClick={() => handleSort(column)}
    >
      <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        <span>{label}</span>
        {sortColumn === column ? (
          sortDirection === "asc" ? (
            <ChevronUpIcon className="w-3 h-3" />
          ) : (
            <ChevronDownIcon className="w-3 h-3" />
          )
        ) : (
          <span className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  // Show warning if we couldn't fetch full data and only have limited jumps
  const hasLimitedData = !fullSession && !isLoadingSession;

  // Fetch user details when tab changes to user-details or privacy
  useEffect(() => {
    if ((activeTab === "user-details" || activeTab === "privacy") && !userDetails && !isLoadingUser) {
      fetchUserDetails();
    }
  }, [activeTab]);

  const fetchUserDetails = async () => {
    setIsLoadingUser(true);
    setUserError(null);
    try {
      const details = await LBSubscriptionService.getStatus(session.user.id);
      setUserDetails(details);
    } catch (err: any) {
      // Provide more context for the error
      const errorMessage = err.userMessage || err.message || "Failed to fetch user details";
      console.error("Failed to fetch user details for user ID:", session.user.id, err);
      setUserError(errorMessage);
    } finally {
      setIsLoadingUser(false);
    }
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: "jumps", label: "Jumps" },
    { id: "user-details", label: "User Details" },
    { id: "privacy", label: "Privacy" },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1100] p-4">
      <div className="bg-gray-900 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowProfilePic(true)}
              className="relative group flex-shrink-0"
              title="View full size"
            >
              <img
                src={profileSrc}
                alt={session.user.name}
                className="w-16 h-16 rounded-full object-cover border-3 border-gray-600 group-hover:border-blue-500 transition-colors cursor-pointer"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = DEFAULT_PROFILE_PIC;
                }}
              />
              <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <svg className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            </button>
            <div>
              <h2 className="text-white font-semibold text-lg flex items-center gap-2">
                {session.user.name}
                {session.user.country && (
                  <img
                    src={`https://flagcdn.com/w20/${session.user.country.toLowerCase()}.png`}
                    alt={session.user.country}
                    className="w-5 h-auto"
                    title={COUNTRY_NAMES[session.user.country.toUpperCase()] || session.user.country}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                {session.user.ispro && (
                  <span className="bg-yellow-500 text-xs font-bold px-1.5 py-0.5 rounded text-black">
                    PRO
                  </span>
                )}
                {/* Online status badge */}
                {(() => {
                  const status = getConnectionStatus(session.lastgpsupdate, session.lastjumpupdate);
                  return (
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${status.bgColor} ${status.textColor}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status.color }} />
                      {status.label}
                    </span>
                  );
                })()}
                {/* Deeplink to Users page */}
                <DeeplinkButton
                  userId={session.user.id}
                  variant="badge"
                  title="Open user in Users page"
                  className="ml-1"
                />
              </h2>
              <div className="flex items-center gap-1.5 text-gray-400 text-sm mt-1">
                <MapPinIcon className="w-4 h-4 text-red-400 flex-shrink-0" />
                {session.spot?.id ? (
                  <a
                    href={`https://discover.thesurfr.app/spots/${encodeURIComponent(session.spot.name.toLowerCase().replace(/\s+/g, '-'))}/${session.spot.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 hover:underline flex items-center gap-1"
                  >
                    {session.spot.name}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ) : (
                  <span>{session.spot?.name || "Unknown location"}</span>
                )}
                {session.spot?.alpha2 && (
                  <>
                    <span className="text-gray-600">•</span>
                    <img
                      src={`https://flagcdn.com/w20/${session.spot.alpha2.toLowerCase()}.png`}
                      alt={session.spot.alpha2}
                      className="w-4 h-auto flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <span className="text-gray-300">{COUNTRY_NAMES[session.spot.alpha2.toUpperCase()] || session.spot.alpha2}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Session Stats (always visible) */}
        <div className="p-4 bg-gray-800/50 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-green-400">{formatHeight(session.maxheight)}m</div>
            <div className="text-xs text-gray-400">Max Height</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-400">
              {isLoadingSession ? (
                <span className="animate-pulse">...</span>
              ) : (
                sortedJumps.length
              )}
            </div>
            <div className="text-xs text-gray-400">Total Jumps</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-400">{formatSessionDuration(session.sessiontime)}</div>
            <div className="text-xs text-gray-400">Duration</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-cyan-400">{getSessionStartTime()}</div>
            <div className="text-xs text-gray-400">Started</div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-700">
          <nav className="flex space-x-1 px-4" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-3 px-4 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? "border-blue-500 text-blue-400"
                    : "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600"
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Jumps Tab */}
          {activeTab === "jumps" && (
            <div>
              {/* Kite & Device Info Row */}
              <div className="mb-4 flex flex-wrap gap-2">
                {/* Kite Info */}
                {session.kite && session.kite.kitebrand?.brand && (
                  <div className="px-3 py-2 bg-gray-800 rounded-lg text-gray-300 text-sm">
                    <span className="text-gray-500">Kite:</span> {session.kite.kitebrand.brand} {session.kite.kitemodel?.model} {session.kite.kitesize?.size}m
                  </div>
                )}

                {/* Device Info with icons */}
                {session.device && (session.device.model || session.device.os || session.device.brand) && (() => {
                  const deviceInfo = getDeviceInfo(session.device);
                  if (!deviceInfo) return null;
                  return (
                    <div className="px-3 py-2 bg-gray-800 rounded-lg text-gray-300 text-sm flex items-center gap-2">
                      <span className="text-gray-500">Device:</span>
                      {/* Device type icon */}
                      {deviceInfo.isWatch ? (
                        <WatchIcon className="w-4 h-4 text-orange-400" />
                      ) : (
                        <PhoneIcon className="w-4 h-4 text-gray-400" />
                      )}
                      {/* OS icon */}
                      {deviceInfo.isApple ? (
                        <AppleIcon className="w-4 h-4 text-gray-300" />
                      ) : (
                        <AndroidIcon className="w-4 h-4 text-green-400" />
                      )}
                      <span>{deviceInfo.label}</span>
                      {deviceInfo.model && (
                        <span className="text-gray-400">({deviceInfo.model})</span>
                      )}
                    </div>
                  );
                })()}
              </div>

              {hasLimitedData && sortedJumps.length > 0 && (
                <div className="mb-3 px-3 py-2 bg-yellow-900/30 border border-yellow-700/50 rounded-lg text-yellow-400 text-xs">
                  Showing limited data. Deploy backend for full jump details.
                </div>
              )}

              {isLoadingSession ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : sortedJumps.length === 0 ? (
                <div className="text-gray-400 text-center py-8">No jumps recorded yet</div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-700">
                  {/* Fixed header */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800 sticky top-0">
                        <tr className="text-gray-400 border-b border-gray-700">
                          <SortHeader column="nr" label="#" />
                          <SortHeader column="time" label="Time" />
                          <SortHeader column="height" label="Height" align="right" />
                          <SortHeader column="airtime" label="Airtime" align="right" />
                          <SortHeader column="distance" label="Distance" align="right" />
                          <SortHeader column="speed" label="Speed" align="right" />
                        </tr>
                      </thead>
                    </table>
                  </div>
                  {/* Scrollable body */}
                  <div className="overflow-y-auto max-h-80">
                    <table className="w-full text-sm">
                      <tbody>
                        {sortedJumps.map((jump, index) => (
                          <tr
                            key={jump.id || index}
                            className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                          >
                            <td className="py-2 px-2 text-gray-400">{jump.nr || index + 1}</td>
                            <td className="py-2 px-2 text-gray-300">
                              {jump.timestring || formatTimestamp(jump.timestampclient)}
                            </td>
                            <td className="py-2 px-2 text-right">
                              <span className="text-green-400 font-medium">{formatHeight(jump.height)}m</span>
                            </td>
                            <td className="py-2 px-2 text-right text-blue-400">{formatAirtime(jump.airtime)}s</td>
                            <td className="py-2 px-2 text-right text-purple-400">{formatDistance(jump.distance)}m</td>
                            <td className="py-2 px-2 text-right text-cyan-400">{formatSpeed(jump.maxspeedreached)} km/h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User Details Tab */}
          {activeTab === "user-details" && (
            <div>
              {isLoadingUser ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : userError ? (
                <div className="text-red-400 text-center py-8">{userError}</div>
              ) : userDetails ? (
                <div className="space-y-5">
                  {/* Account Info Header */}
                  {userDetails.createdAt && (
                    <div className="bg-gray-800/50 rounded-lg px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-cyan-600/20 flex items-center justify-center">
                        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Member since</p>
                        <p className="text-sm font-medium text-gray-200">
                          {new Date(userDetails.createdAt).toLocaleDateString()} {new Date(userDetails.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Quick Stats Row */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-gray-800 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-gray-100">{userDetails.sessionsCount ?? 0}</p>
                      <p className="text-xs text-gray-500">Sessions</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-gray-100">{userDetails.kiteCount ?? 0}</p>
                      <p className="text-xs text-gray-500">Kites</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-gray-100">{userDetails.followersCount ?? 0}</p>
                      <p className="text-xs text-gray-500">Followers</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-gray-100">{userDetails.followingCount ?? 0}</p>
                      <p className="text-xs text-gray-500">Following</p>
                    </div>
                  </div>

                  {/* Profile and Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Email</p>
                      <p className="text-sm text-gray-200">{userDetails.username || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Display Name</p>
                      <p className="text-sm text-gray-200">{userDetails.name || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">First Name</p>
                      <p className="text-sm text-gray-200">{userDetails.firstName || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Last Name</p>
                      <p className="text-sm text-gray-200">{userDetails.lastName || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Gender</p>
                      <p className="text-sm text-gray-200">{userDetails.gender || "-"}</p>
                    </div>
                    {userDetails.preferredSport && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Sport</p>
                        <p className="text-sm text-gray-200 capitalize">{userDetails.preferredSport}</p>
                      </div>
                    )}
                    {userDetails.skillLevel && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Skill Level</p>
                        <p className="text-sm text-gray-200 capitalize">{userDetails.skillLevel}</p>
                      </div>
                    )}
                    {userDetails.homeSpotName && (
                      <div className="col-span-2">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Home Spot</p>
                        {userDetails.homeSpotId ? (
                          <a
                            href={`https://discover.thesurfr.app/spots/${encodeURIComponent(userDetails.homeSpotName.toLowerCase().replace(/\s+/g, '-'))}/${userDetails.homeSpotId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-cyan-400 hover:text-cyan-300 hover:underline flex items-center gap-1"
                          >
                            {userDetails.homeSpotName}
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        ) : (
                          <p className="text-sm text-gray-200">{userDetails.homeSpotName}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Personal Records */}
                  <div className="pt-4 border-t border-gray-700">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Personal Records</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/30 rounded-lg p-3">
                        <p className="text-xs text-gray-400">Max Height</p>
                        <p className="text-lg font-bold text-green-400">
                          {userDetails.maxheight ? `${userDetails.maxheight.toFixed(1)}m` : "-"}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-700/30 rounded-lg p-3">
                        <p className="text-xs text-gray-400">Max Airtime</p>
                        <p className="text-lg font-bold text-blue-400">
                          {userDetails.maxairtime ? `${userDetails.maxairtime.toFixed(1)}s` : "-"}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-700/30 rounded-lg p-3">
                        <p className="text-xs text-gray-400">Max Distance</p>
                        <p className="text-lg font-bold text-purple-400">
                          {userDetails.maxdistance ? `${userDetails.maxdistance.toFixed(0)}m` : "-"}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/20 border border-cyan-700/30 rounded-lg p-3">
                        <p className="text-xs text-gray-400">Max Speed</p>
                        <p className="text-lg font-bold text-cyan-400">
                          {userDetails.maxspeed ? `${(userDetails.maxspeed * 3.6).toFixed(0)}km/h` : "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Social Media */}
                  <div className="pt-4 border-t border-gray-700">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Social Media</h3>
                    {!userDetails.socialInstagram && !userDetails.socialTiktok ? (
                      <p className="text-sm text-gray-500 italic">No social media accounts configured</p>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        {userDetails.socialInstagram && (
                          <a
                            href={`https://instagram.com/${userDetails.socialInstagram}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 px-3 py-1.5 rounded-full text-white text-sm hover:opacity-90 transition-opacity"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                            @{userDetails.socialInstagram}
                          </a>
                        )}
                        {userDetails.socialTiktok && (
                          <a
                            href={`https://tiktok.com/@${userDetails.socialTiktok}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-gray-800 px-3 py-1.5 rounded-full text-white text-sm hover:bg-gray-700 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                            </svg>
                            @{userDetails.socialTiktok}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === "privacy" && (
            <div>
              {isLoadingUser ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : userError ? (
                <div className="text-red-400 text-center py-8">{userError}</div>
              ) : userDetails ? (
                <div className="space-y-4">
                  <h3 className="text-base font-medium text-gray-200">Privacy Settings</h3>

                  {/* Privacy Badges */}
                  <div className="flex flex-wrap gap-3">
                    {/* LB Visible Badge */}
                    <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${
                      userDetails.lbVisible ? "bg-green-600 text-white" : "bg-gray-600 text-gray-300"
                    }`}>
                      {userDetails.lbVisible ? (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                      {userDetails.lbVisible ? "LB Visible" : "LB Hidden"}
                    </span>

                    {/* Live Scope Badge */}
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-600 text-white">
                      {userDetails.liveScope === "everyone" ? (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      ) : userDetails.liveScope === "nobody" ? (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                      Live: {userDetails.liveScope || "followers"}
                    </span>

                    {/* Private/Public Badge */}
                    <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${
                      userDetails.privateAccount ? "bg-yellow-600 text-white" : "bg-green-600 text-white"
                    }`}>
                      {userDetails.privateAccount ? (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                        </svg>
                      )}
                      {userDetails.privateAccount ? "Private" : "Public"}
                    </span>
                  </div>

                  {/* Explanatory Text */}
                  <div className="mt-6 space-y-3 text-sm">
                    <div className="bg-gray-800 rounded-lg p-3">
                      <p className="text-gray-400">
                        <span className="font-medium text-gray-300">LB Visible:</span>{" "}
                        {userDetails.lbVisible
                          ? "User's profile is visible on the leaderboard."
                          : "User's profile is hidden from the leaderboard."}
                      </p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-3">
                      <p className="text-gray-400">
                        <span className="font-medium text-gray-300">Live Scope:</span>{" "}
                        {userDetails.liveScope === "everyone"
                          ? "Live sessions are visible to everyone."
                          : userDetails.liveScope === "nobody"
                          ? "Live sessions are not visible to anyone."
                          : "Live sessions are visible to followers only."}
                      </p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-3">
                      <p className="text-gray-400">
                        <span className="font-medium text-gray-300">Account Type:</span>{" "}
                        {userDetails.privateAccount
                          ? "Private account - only approved followers can see content."
                          : "Public account - content is visible to all users."}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-between items-center text-xs text-gray-400">
          <div>
            Last GPS: {formatTimestamp(session.lastgpsupdate as number)}
          </div>
          <div>
            Last Jump: {formatTimestamp(session.lastjumpupdate as number)}
          </div>
        </div>
      </div>

      {/* Full-size Profile Picture Modal */}
      {showProfilePic && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[1200] p-4"
          onClick={() => setShowProfilePic(false)}
        >
          <div className="relative max-w-lg w-full">
            <button
              onClick={() => setShowProfilePic(false)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="w-8 h-8" />
            </button>
            <img
              src={profileSrc}
              alt={session.user.name}
              className="w-full h-auto rounded-xl object-cover shadow-2xl"
              onError={(e) => {
                (e.target as HTMLImageElement).src = DEFAULT_PROFILE_PIC;
              }}
            />
            <div className="mt-4 text-center">
              <p className="text-white text-lg font-semibold">{session.user.name}</p>
              {session.user.username && (
                <p className="text-gray-400 text-sm">@{session.user.username}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JumpDetailsModal;
